/**
 * @module web/mediaManager
 * @private
 */

/**
 * This module exposes decorator methods that wrap mediaManager methods that retrieve media content and adds caching
 * capabilities to them, without having to modify the mediaManager methods themselves with cache logic.
 */

import { cache } from "../../persistence/cache";
import { memoryCache } from "../../persistence/memoryCache";
import { token } from "../../store";

const TITLES = "titles";
const SEARCH = "search";
const FEEDS = "feeds";
const GENRES_LOGGED = "genres-logged";
const GENRES = "genres";

// Default values for feeds Paginations
const defaultFeedsPagination = {
  from: "1",
  to: "60"
};

/**
 * Extends mediaManager.getMovies and mediaManager.getSeries methods to check if the requested title is already stored
 * in the cache, and will use the local version instead of making the call to the backend.
 * @param {function} originalFunction mediaManager.getMovies, mediaManager.getSeries or mediaManager.getTitles method that will be wrapped with
 * the previous cache check.
 * @returns {function} A new getMovies or getSeries function that will check the local cache before making a network
 * request without changing the original method itself.
 * @private
 */
export function fromTitlesCache(originalFunction) {
  return async function(id, config) {
    if (this.localCache && this.localCache.titles) {
      let title = await cache.get(id, { database: TITLES });
      if (!title || (config.is4K && !title.title4K)) {
        return await originalFunction.call(this, id, config);
      }
      return title;
    } else {
      return await originalFunction.call(this, id, config);
    }
  };
}

/**
 * Extends mediaManager.getMovies and mediaManager.getSeries methods to store the received data in the local cache after
 * the network request.
 * @param {function} originalFunction mediaManager.getSeries, mediaManager.getMovies or mediaManager.getTitles methods that will be wrapped with
 * the caching logic
 * @returns {function} A new getSeries or getMovies function that will store the received data in the cache without
 * modifying the original method itself.
 * @private
 */
export function toTitlesCache(originalFunction) {
  return async function(id, config) {
    const title = await originalFunction.call(this, id, config);
    if (this.localCache && this.localCache.titles) {
      cache.set(id, title, {
        database: TITLES,
        expirationDate: title.mediaContent[0].expirationDate
      });
    }
    return title;
  };
}

/**
 * Extends mediaManager.getTitle method to check if the requested data is already stored in memory, and will use
 * the local version instead of making the call to the backend.
 * @param {function} originalFunction mediaManager.getTitle method that will be wrapped with the previous memory check
 * @returns {function} A new getTitle function that will check the memory before making a network request without
 * changing the getTitle method itself.
 */
export function fromTitleMemoryCache(originalFunction) {
  return async function(id, config) {
    if (this.localCache && this.localCache.memoryCache) {
      const title = memoryCache.get(id);
      if (!title || (config.is4K && !title.title4K)) {
        return await originalFunction.call(this, id, config);
      }
      return title;
    } else {
      return await originalFunction.call(this, id, config);
    }
  };
}

/**
 * Extends mediaManager.getTitle method to store the received data in memory after the network request.
 * @param {function} originalFunction mediaManager.getTitle method that will be wrapped with the memoizing logic
 * @returns {function} A new getTitle function that will store the received data in memory without modifying
 * the getTitle method itself.
 */
export function toTitleMemoryCache(originalFunction) {
  return async function(id, is4K = false) {
    const title = await originalFunction.call(this, id, is4K);
    if (this.localCache && this.localCache.memoryCache) {
      memoryCache.set(id, title, {
        expiresIn: this.titlesCacheTime
      });
    }
    return title;
  };
}

function getSeasonsListKey(seriesId) {
  return `${seriesId}-seasonsList`;
}

/**
 * Extends mediaManager.getSeriesSeasonsList method to check if the requested resource is already stored in the cache,
 * and will use the local version instead of making the call to the backend.
 * @param {function} originalFunction mediaManager.getSeriesSeasonsList method that will be wrapped with the previous
 * cache check.
 * @returns {function} A new getSeriesSeasonsList function that will check the local cache before making a network
 * request without changing the getSeriesSeasonsList method itself.
 * @private
 */
export function fromSeasonsCache(originalFunction) {
  return async function(seriesId) {
    if (this.localCache && this.localCache.titles) {
      const key = getSeasonsListKey(seriesId);
      let seasonsList = await cache.get(key, {
        database: TITLES
      });
      if (!seasonsList) {
        return await originalFunction.call(this, seriesId);
      }
      return seasonsList;
    } else {
      return await originalFunction.call(this, seriesId);
    }
  };
}

/**
 * Extends mediaManager.getSeriesSeasonsList method to store the received data in the local cache after the network
 * request.
 * @param {function} originalFunction mediaManager.getSeriesSeasonsList method that will be wrapped with the caching
 * logic
 * @returns {function} A new getSeriesSeasonsList function that will store the received data in the cache without
 * modifying the original method itself.
 * @private
 */
export function toSeasonsCache(originalFunction) {
  return async function(seriesId) {
    let seasonsList = await originalFunction.call(this, seriesId);
    if (this.localCache && this.localCache.titles) {
      const key = getSeasonsListKey(seriesId);
      cache.set(key, seasonsList, {
        database: TITLES,
        expiresIn: this.seasonsCacheTime
      });
    }
    return seasonsList;
  };
}

/**
 * Extends mediaManager.getSeriesSeasonsList method to check if the requested data is already stored in memory, and will use
 * the local version instead of making the call to the backend.
 * @param {function} originalFunction mediaManager.getSeriesSeasonsList method that will be wrapped with the previous memory check
 * @returns {function} A new getSeriesSeasonsList function that will check the memory before making a network request without
 * changing the getSeriesSeasonsList method itself.
 */
export function fromSeasonsMemoryCache(originalFunction) {
  return async function(seriesId) {
    const key = getSeasonsListKey(seriesId);
    if (this.localCache && this.localCache.memoryCache) {
      return memoryCache.get(key) || (await originalFunction.call(this, seriesId));
    } else {
      return await originalFunction.call(this, seriesId);
    }
  };
}

/**
 * Extends mediaManager.getSeriesSeasonsList method to store the received data in memory after the network request.
 * @param {function} originalFunction mediaManager.getSeriesSeasonsList method that will be wrapped with the memoizing logic
 * @returns {function} A new getSeriesSeasonsList function that will store the received data in memory without modifying
 * the getSeriesSeasonsList method itself.
 */
export function toSeasonsMemoryCache(originalFunction) {
  return async function(seriesId) {
    let seasonsList = await originalFunction.call(this, seriesId);
    const key = getSeasonsListKey(seriesId);
    if (this.localCache && this.localCache.memoryCache) {
      memoryCache.set(key, seasonsList, {
        expiresIn: this.seasonsCacheTime
      });
    }
    return seasonsList;
  };
}

/**
 * Returns key for episodes cache name
 * @param {string} seriesId Identifier of the serie
 * @param {number} seasonNo Number of sesion
 */
function getEpisodesKey(seriesId, seasonNo) {
  return `${seriesId}-season${seasonNo}-episodes`;
}

/**
 * Extends mediaManager.getSeriesSeasonEpisodes method to check if the requested resource is already stored in the
 * cache, and will use the local version instead of making the call to the backend.
 * @param {function} originalFunction mediaManager.getSeriesSeasonEpisodes method that will be wrapped with the previous
 * cache check.
 * @returns {function} A new getSeriesSeasonEpisodes or getSeriesSeasonEpisode function that will check the local cache
 * before making a network request without changing the original method itself.
 * @private
 */
export function fromEpisodesCache(originalFunction) {
  return async function(seriesId, seasonNo) {
    if (this.localCache && this.localCache.titles) {
      const key = getEpisodesKey(seriesId, seasonNo);
      let episodesList = await cache.get(key, { database: TITLES });
      if (!episodesList) {
        return await originalFunction.call(this, seriesId, seasonNo);
      }
      return episodesList;
    } else {
      return await originalFunction.call(this, seriesId, seasonNo);
    }
  };
}

/**
 * Extends mediaManager.getSeriesSeasonEpisodes method to store the received data in the local cache after the network
 * request.
 * @param {function} originalFunction mediaManager.getSeriesSeasonEpisodes method that will be wrapped with the caching
 * logic
 * @returns {function} A new getSeriesSeasonEpisodes function that will store the received data in the cache without
 * modifying the original method itself.
 * @private
 */
export function toEpisodesCache(originalFunction) {
  return async function(seriesId, seasonNo) {
    let episodesList = await originalFunction.call(this, seriesId, seasonNo);
    if (this.localCache && this.localCache.titles) {
      const key = getEpisodesKey(seriesId, seasonNo);
      cache.set(key, episodesList, {
        database: TITLES,
        expiresIn: this.episodesCacheTime
      });
    }
    return episodesList;
  };
}

/**
 * Extends mediaManager.getSeriesSeasonEpisodes method to check if the requested data is already stored in memory, and will use
 * the local version instead of making the call to the backend.
 * @param {function} originalFunction mediaManager.getSeriesSeasonEpisodes method that will be wrapped with the previous memory check
 * @returns {function} A new getSeriesSeasonEpisodes function that will check the memory before making a network request without
 * changing the getSeriesSeasonEpisodes method itself.
 */
export function fromEpisodesMemoryCache(originalFunction) {
  return async function(seriesId, seasonNo) {
    const key = getEpisodesKey(seriesId, seasonNo);
    if (this.localCache && this.localCache.memoryCache) {
      return memoryCache.get(key) || (await originalFunction.call(this, seriesId, seasonNo));
    } else {
      return await originalFunction.call(this, seriesId, seasonNo);
    }
  };
}

/**
 * Extends mediaManager.getSeriesSeasonEpisodes method to store the received data in memory after the network request.
 * @param {function} originalFunction mediaManager.getSeriesSeasonEpisodes method that will be wrapped with the memoizing logic
 * @returns {function} A new getSeriesSeasonEpisodes function that will store the received data in memory without modifying
 * the getSeriesSeasonEpisodes method itself.
 */
export function toEpisodesMemoryCache(originalFunction) {
  return async function(seriesId, seasonNo) {
    let episodesList = await originalFunction.call(this, seriesId, seasonNo);
    const key = getEpisodesKey(seriesId, seasonNo);
    if (this.localCache && this.localCache.memoryCache) {
      memoryCache.set(key, episodesList, {
        expiresIn: this.episodesCacheTime
      });
    }
    return episodesList;
  };
}

/**
 * Extends mediaManager.getSeriesSeasonEpisode method to check if the requested resource is already stored in the cache,
 * and will use the local version instead of making the call to the backend.
 * @param {function} originalFunction mediaManager.getSeriesSeasonEpisode method that will be wrapped with the previous
 * cache check.
 * @returns {function} A new getSeriesSeasonEpisode function that will check the local cache before making a network
 * request without changing the original method itself.
 * @private
 */
export function fromEpisodeCache(originalFunction) {
  return async function(seriesId, seasonNo, episodeNo) {
    if (this.localCache && this.localCache.titles) {
      const key = getEpisodesKey(seriesId, seasonNo);
      let episodesList = await cache.get(key, { database: TITLES });
      let episode = null;
      if (episodesList && episodesList[episodeNo - 1]) {
        episode = episodesList[episodeNo - 1];
      } else {
        episode = originalFunction.call(this, seriesId, seasonNo, episodeNo);
      }
      return episode;
    } else {
      return await originalFunction.call(this, seriesId, seasonNo, episodeNo);
    }
  };
}

/**
 * Extends mediaManager.getGenre method to check if the requested genres is already stored in the cache, and will use
 * the local version instead of making the call to the backend.
 * @param {function} originalFunction mediaManager.getGenres method that will be wrapped with the previous cache check
 * @returns {function} A new getGenres function that will check the local cache before making a network request without
 * changing the getGenres method itself.
 * @private
 */
export function fromGenresCache(originalFunction) {
  return async function(params) {
    if (this.localCache && this.localCache.genres) {
      const isLoggedIn = await token.getToken();
      const key = isLoggedIn ? GENRES_LOGGED : GENRES;
      let genres = await cache.get(key, { database: GENRES });
      if (!genres) {
        genres = await originalFunction.call(this, params);
      }
      return genres;
    } else {
      return await originalFunction.call(this, params);
    }
  };
}

/**
 * Extends mediaManager.getGenres method to store the received data in the local cache after the network request.
 * @param {function} originalFunction mediaManager.getGenres method that will be wrapped with the caching logic
 * @returns {function} A new getGenres function that will store the received data in the cache without modifying
 * the getGenres method itself.
 * @private
 */
export function toGenresCache(originalFunction) {
  return async function(params) {
    const genres = await originalFunction.call(this, params);
    if (this.localCache && this.localCache.genres) {
      const isLoggedIn = await token.getToken();
      const key = isLoggedIn ? GENRES_LOGGED : GENRES;
      cache.set(key, genres, {
        database: GENRES,
        expiresIn: this.genresCacheTime
      });
    }
    return genres;
  };
}

/**
 * Returns key for catalogue cache name
 * @param {string} id Identifier of the catalog item
 * @param {object} params Extra params
 */
async function getCatalogueItemKey(id, params = {}) {
  const { from = defaultFeedsPagination.from, to = defaultFeedsPagination.to, sort = "alphabetical" } = params;
  const isLoggedIn = await token.getToken();
  return `${isLoggedIn ? GENRES_LOGGED : GENRES}-${id}-${from}-${to}-${sort}`;
}

/**
 * Extends mediaManager.getCatalogueItems method to check if the requested genres is already stored in the cache, and will use
 * the local version instead of making the call to the backend.
 * @param {function} originalFunction mediaManager.getCatalogueItems method that will be wrapped with the previous cache check
 * @returns {function} A new getCatalogueItems function that will check the local cache before making a network request without
 * changing the getCatalogueItems method itself.
 * @private
 */
export function fromCatalogueItemCache(originalFunction) {
  return async function(sectionName, params = {}) {
    if (this.localCache && this.localCache.feeds) {
      // Consider if the getFeedsItems requested without the optional params
      const key = await getCatalogueItemKey(sectionName, params);
      let feeds = await cache.get(key, { database: FEEDS });
      if (!feeds) {
        feeds = await originalFunction.call(this, sectionName, params);
      }
      return feeds;
    } else {
      return await originalFunction.call(this, sectionName, params);
    }
  };
}

/**
 * Extends mediaManager.getCatalogueItems method to store the received data in the local cache after the network request.
 * @param {function} originalFunction mediaManager.getCatalogueItems method that will be wrapped with the caching logic
 * @returns {function} A new getCatalogueItems function that will store the received data in the cache without modifying
 * the getCatalogueItems method itself.
 * @private
 */
export function toCatalogueItemCache(originalFunction) {
  return async function(sectionName, params = {}) {
    const feeds = await originalFunction.call(this, sectionName, params);
    if (this.localCache && this.localCache.feeds) {
      // Consider if the getFeedsItems requested without the optional params
      const key = await getCatalogueItemKey(sectionName, params);
      cache.set(key, feeds, {
        database: FEEDS,
        expiresIn: this.feedsCacheTime
      });
    }
    return feeds;
  };
}

/**
 * Extends mediaManager.getCatalogueItem method to check if the requested data is already stored in memory, and will use
 * the local version instead of making the call to the backend.
 * @param {function} originalFunction mediaManager.getCatalogueItem method that will be wrapped with the previous memory check
 * @returns {function} A new getCatalogueItem function that will check the memory before making a network request without
 * changing the getCatalogueItem method itself.
 */
export function fromCatalogueItemMemoryCache(originalFunction) {
  return async function(sectionName, params = {}) {
    if (this.localCache && this.localCache.memoryCache) {
      const key = await getCatalogueItemKey(sectionName, params);
      return memoryCache.get(key) || (await originalFunction.call(this, sectionName, params));
    } else {
      return await originalFunction.call(this, sectionName, params);
    }
  };
}

/**
 * Extends mediaManager.getCatalogueItem method to store the received data in memory after the network request.
 * @param {function} originalFunction mediaManager.getCatalogueItem method that will be wrapped with the memoizing logic
 * @returns {function} A new getCatalogueItem function that will store the received data in memory without modifying
 * the getCatalogueItem method itself.
 */
export function toCatalogueItemMemoryCache(originalFunction) {
  return async function(sectionName, params = {}) {
    const response = await originalFunction.call(this, sectionName, params);
    if (this.localCache && this.localCache.memoryCache) {
      const key = await getCatalogueItemKey(sectionName, params);
      memoryCache.set(key, response, {
        expiresIn: this.feedsCacheTime
      });
    }
    return response;
  };
}

/**
 * Extends mediaManager.getCatalogueItemsByGenreId method to check if the requested genres is already stored in the cache, and will use
 * the local version instead of making the call to the backend.
 * @param {function} originalFunction mediaManager.getCatalogueItemsByGenreId method that will be wrapped with the previous cache check
 * @returns {function} A new getCatalogueItemsByGenreId function that will check the local cache before making a network request without
 * changing the getCatalogueItemsByGenreId method itself.
 * @private
 */
export function fromCatalogueItemByGenreIdCache(originalFunction) {
  return async function(genreTagId, params = {}) {
    if (this.localCache && this.localCache.feeds) {
      // Consider if the getFeedsItems requested without the optional params
      const key = await getCatalogueItemKey(genreTagId, params);
      let feeds = await cache.get(key, { database: FEEDS });
      if (!feeds) {
        feeds = await originalFunction.call(this, genreTagId, params);
      }
      return feeds;
    } else {
      return await originalFunction.call(this, genreTagId, params);
    }
  };
}

/**
 * Extends mediaManager.getCatalogueItemsByGenreId method to store the received data in the local cache after the network request.
 * @param {function} originalFunction mediaManager.getCatalogueItemsByGenreId method that will be wrapped with the caching logic
 * @returns {function} A new getCatalogueItemsByGenreId function that will store the received data in the cache without modifying
 * the getCatalogueItemsByGenreId method itself.
 * @private
 */
export function toCatalogueItemByGenreIdCache(originalFunction) {
  return async function(genreTagId, params = {}) {
    const feeds = await originalFunction.call(this, genreTagId, params);
    if (this.localCache && this.localCache.feeds) {
      // Consider if the getFeedsItems requested without the optional params
      const key = await getCatalogueItemKey(genreTagId, params);
      cache.set(key, feeds, {
        database: FEEDS,
        expiresIn: this.feedsCacheTime
      });
    }
    return feeds;
  };
}

/**
 * Extends mediaManager.getCatalogueItemsByGenreId method to check if the requested data is already stored in memory, and will use
 * the local version instead of making the call to the backend.
 * @param {function} originalFunction mediaManager.getCatalogueItemsByGenreId method that will be wrapped with the previous memory check
 * @returns {function} A new getCatalogueItemsByGenreId function that will check the memory before making a network request without
 * changing the getCatalogueItemsByGenreId method itself.
 */
export function fromCatalogueItemByGenreIdMemoryCache(originalFunction) {
  return async function(genreTagId, params = {}) {
    if (this.localCache && this.localCache.memoryCache) {
      const key = await getCatalogueItemKey(genreTagId, params);
      return memoryCache.get(key) || (await originalFunction.call(this, genreTagId, params));
    } else {
      return await originalFunction.call(this, genreTagId, params);
    }
  };
}

/**
 * Extends mediaManager.getCatalogueItemsByGenreId method to store the received data in memory after the network request.
 * @param {function} originalFunction mediaManager.getCatalogueItemsByGenreId method that will be wrapped with the memoizing logic
 * @returns {function} A new getCatalogueItemsByGenreId function that will store the received data in memory without modifying
 * the getCatalogueItemsByGenreId method itself.
 */
export function toCatalogueItemByGenreIdMemoryCache(originalFunction) {
  return async function(genreTagId, params = {}) {
    const response = await originalFunction.call(this, genreTagId, params);
    if (this.localCache && this.localCache.memoryCache) {
      const key = await getCatalogueItemKey(genreTagId, params);
      memoryCache.set(key, response, {
        expiresIn: this.feedsCacheTime
      });
    }
    return response;
  };
}

export function clearMediaCaches(originalFunction) {
  return function(...params) {
    if (this.localCache) {
      if (this.localCache.titles) {
        cache.clear({ database: TITLES });
      }
      if (this.localCache.search) {
        cache.clear({ database: SEARCH });
      }
      if (this.localCache.feeds) {
        cache.clear({ database: FEEDS });
      }
      if (this.localCache.genres) {
        cache.clear({ database: GENRES });
      }
      if (this.localCache.memoryCache) {
        memoryCache.clear();
      }
    }
    return originalFunction.call(this, ...params);
  };
}
